import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,

} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { WarehouseType } from '@domain/inventory/models/WarehouseType';
import { Paper } from '@shared/ui/common/paper';
import { InfoActionMessage } from '@shared/ui/common/InfoActionMessage';
import CreateWarehouseBottomSheet from './create/CreateWarehouseBottomSheet';
import { useWarehouses } from '../hooks/useWarehouseOperations';
import { Icon } from '@shared/icons';

interface CreateWarehouseForm {
  name: string;
  type: WarehouseType;
  marketplaceConnectionId?: string;
  location?: string;
  active: boolean;
}

const Warehouses = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [form, setForm] = useState<CreateWarehouseForm>({
    name: '',
    type: WarehouseType.STANDARD,
    active: true,
  });

  const { warehouses } = useWarehouses();

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
  };

  // Check URL parameters to determine if dialog should be open
  useEffect(() => {
    const createParam = searchParams.get('create');
    if (createParam === 'warehouse') {
      setIsDialogOpen(true);
    } else {
      setIsDialogOpen(false);
    }
  }, [searchParams]);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setForm({ name: '', type: WarehouseType.STANDARD, active: true });
  };

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center">
        <Button
          variant="text"
          color="secondary"
          startIcon={<Icon icon="Plus01" />}
          onClick={handleOpenDialog}
        >
          New Warehouse
        </Button>
      </Stack>

      <Paper>
        {warehouses?.length === 0 ? (
          <InfoActionMessage
            title="No Warehouses"
            description="Get started by creating your first warehouse"
            buttonText="Create Warehouse"
            onButtonClick={() => handleOpenDialog()}
          />
        ) : (

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {warehouses?.map((warehouse) => (
                  <TableRow
                    key={warehouse.id}
                    hover
                    onClick={() => navigate(`/inventory/warehouses/${warehouse.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{warehouse.name}</TableCell>
                    <TableCell>{warehouse.location}</TableCell>
                    <TableCell>
                      <Chip
                        label={warehouse.active ? 'Active' : 'Inactive'}
                        color={warehouse.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

        )}
      </Paper>

      <CreateWarehouseBottomSheet
        open={isDialogOpen}
        onClose={handleCloseDialog}
      />
    </Stack>
  );
};

export default Warehouses; 